(function () {
  if (window.cartFreeShippingAddonsInitialized) return;
  window.cartFreeShippingAddonsInitialized = true;

  if (!customElements.get('cart-free-shipping-addons')) {
    customElements.define(
      'cart-free-shipping-addons',
      class CartFreeShippingAddons extends HTMLElement {
        constructor() {
          super();
          this.currentIndex = 0;
          this.snapPoints = [];
          this.scrollFrame = null;
          this.resizeObserver = null;
          this.handleResize = this.handleResize.bind(this);
          this.handleScroll = this.handleScroll.bind(this);
          this.handlePrevClick = this.handlePrevClick.bind(this);
          this.handleNextClick = this.handleNextClick.bind(this);
          this.handleProgressInput = this.handleProgressInput.bind(this);
        }

        connectedCallback() {
          this.viewport = this.querySelector('[data-addon-viewport]') || this.querySelector('.cart-free-shipping-addons__viewport');
          this.track = this.querySelector('[data-addon-track]');
          this.cards = Array.from(this.querySelectorAll('[data-addon-card]'));
          this.controls = this.querySelector('[data-addon-controls]');
          this.prevButton = this.querySelector('[data-addon-prev]');
          this.nextButton = this.querySelector('[data-addon-next]');
          this.progressWrap = this.querySelector('[data-addon-progress-wrap]');
          this.progress = this.querySelector('[data-addon-progress]');
          this.status = this.querySelector('[data-addon-status]');

          if (!this.viewport || this.cards.length === 0) return;

          this.prevButton?.addEventListener('click', this.handlePrevClick);
          this.nextButton?.addEventListener('click', this.handleNextClick);
          this.progress?.addEventListener('input', this.handleProgressInput);
          this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
          window.addEventListener('resize', this.handleResize);

          if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(() => this.update({ alignToCurrent: true }));
            this.resizeObserver.observe(this.viewport);
          }

          requestAnimationFrame(() => this.update({ alignToCurrent: true }));
        }

        disconnectedCallback() {
          this.prevButton?.removeEventListener('click', this.handlePrevClick);
          this.nextButton?.removeEventListener('click', this.handleNextClick);
          this.progress?.removeEventListener('input', this.handleProgressInput);
          this.viewport?.removeEventListener('scroll', this.handleScroll);
          window.removeEventListener('resize', this.handleResize);

          if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
          if (this.resizeObserver) this.resizeObserver.disconnect();
        }

        get maxIndex() {
          return Math.max(0, this.snapPoints.length - 1);
        }

        buildSnapPoints() {
          const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
          const points = [];

          this.cards.forEach((card) => {
            const clampedPoint = Math.min(card.offsetLeft, maxScroll);
            const previousPoint = points[points.length - 1];

            if (typeof previousPoint === 'undefined' || Math.abs(previousPoint - clampedPoint) > 1) {
              points.push(clampedPoint);
            }
          });

          this.snapPoints = points.length ? points : [0];
        }

        findNearestIndex(scrollLeft) {
          let nearestIndex = 0;
          let nearestDistance = Number.POSITIVE_INFINITY;

          this.snapPoints.forEach((point, index) => {
            const distance = Math.abs(point - scrollLeft);
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearestIndex = index;
            }
          });

          return nearestIndex;
        }

        handleResize() {
          this.update({ alignToCurrent: true });
        }

        handleScroll() {
          if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = requestAnimationFrame(() => {
            this.currentIndex = this.findNearestIndex(this.viewport.scrollLeft);
            this.updateControls();
          });
        }

        handlePrevClick() {
          this.setIndex(this.currentIndex - 1);
        }

        handleNextClick() {
          this.setIndex(this.currentIndex + 1);
        }

        handleProgressInput(event) {
          this.setIndex(parseInt(event.target.value, 10) || 0);
        }

        setIndex(index) {
          this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
          this.scrollToIndex(this.currentIndex);
        }

        scrollToIndex(index, behavior = 'smooth') {
          const targetPoint = this.snapPoints[index] || 0;

          this.currentIndex = Math.max(0, Math.min(index, this.maxIndex));
          this.viewport.scrollTo({ left: targetPoint, behavior });
          this.updateControls();
        }

        update({ alignToCurrent = false } = {}) {
          if (!this.viewport || this.cards.length === 0) return;

          const currentScrollLeft = this.viewport.scrollLeft;
          this.buildSnapPoints();

          if (alignToCurrent) {
            this.currentIndex = this.findNearestIndex(currentScrollLeft);
          } else {
            this.currentIndex = Math.max(0, Math.min(this.currentIndex, this.maxIndex));
          }

          this.updateControls();

          const targetPoint = this.snapPoints[this.currentIndex] || 0;
          if (Math.abs(currentScrollLeft - targetPoint) > 1) {
            this.viewport.scrollLeft = targetPoint;
          }
        }

        updateControls() {
          const canSlide = this.maxIndex > 0;

          if (this.controls) this.controls.hidden = !canSlide;
          if (this.progressWrap) this.progressWrap.hidden = !canSlide;
          if (this.prevButton) this.prevButton.disabled = this.currentIndex === 0;
          if (this.nextButton) this.nextButton.disabled = this.currentIndex >= this.maxIndex;

          if (this.progress) {
            this.progress.max = String(this.maxIndex);
            this.progress.value = String(this.currentIndex);
          }

          if (this.status) {
            this.status.textContent = canSlide
              ? `Showing pick ${this.currentIndex + 1} of ${this.cards.length}`
              : '';
          }
        }
      }
    );
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-cart-free-shipping-addon-form]')) return;

    event.preventDefault();

    const submitButton = form.querySelector('button[type="submit"]');
    const errorMessage = form.querySelector('[data-cart-free-shipping-addon-error]');

    if (!submitButton || submitButton.getAttribute('aria-disabled') === 'true') return;

    if (errorMessage) {
      errorMessage.hidden = true;
      errorMessage.textContent = '';
    }

    submitButton.setAttribute('aria-disabled', 'true');
    submitButton.classList.add('loading');

    const spinner = submitButton.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');

    const formData = new FormData(form);
    const variantId = formData.get('id');
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    try {
      const response = await fetch(routes.cart_add_url, config).then((res) => res.json());

      if (response.status) {
        if (errorMessage) {
          errorMessage.textContent = response.description || window.cartStrings.error;
          errorMessage.hidden = false;
        }
        return;
      }

      await publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-free-shipping-addons',
        productVariantId: variantId,
        cartData: response,
      });

      const cart = await fetch('/cart.js').then((res) => res.json());
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } catch (error) {
      console.error(error);
      if (errorMessage) {
        errorMessage.textContent = window.cartStrings.error;
        errorMessage.hidden = false;
      }
    } finally {
      submitButton.classList.remove('loading');
      submitButton.removeAttribute('aria-disabled');
      if (spinner) spinner.classList.add('hidden');
    }
  });
})();
